import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../services/api";

/**
 * Passwordless sign-in for a private access link (/access/:token).
 *
 * Two steps: confirm which inbox the code goes to, then type the code. The link
 * alone is not a credential — anyone can open this page, but only whoever reads
 * the invited mailbox can finish, which is what makes it safe to email.
 *
 * Mounted as a PUBLIC route, since this screen is how a guest signs in.
 */
export default function GuestAccessPage({ auth }) {
  const { token } = useParams();
  const navigate = useNavigate();

  // "loading" → "request" → "code" | "dead"
  const [stage, setStage] = useState("loading");
  const [info, setInfo] = useState(null);
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);
  const [resendIn, setResendIn] = useState(0);
  const codeRef = useRef(null);

  // Is this link real, and whose inbox does it point at?
  useEffect(() => {
    let cancelled = false;
    api
      .accessLinkLookup(token)
      .then((res) => {
        if (cancelled) return;
        setInfo(res.data);
        setStage("request");
      })
      .catch((err) => {
        if (cancelled) return;
        setError(
          err.response?.data?.error ||
            "This access link could not be opened. Ask for a new one.",
        );
        setStage("dead");
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  // Mirror the server's own resend throttle so the button is disabled rather
  // than clicked into a 429.
  useEffect(() => {
    if (resendIn <= 0) return;
    const t = setTimeout(() => setResendIn((n) => n - 1), 1000);
    return () => clearTimeout(t);
  }, [resendIn]);

  useEffect(() => {
    if (stage === "code") codeRef.current?.focus();
  }, [stage]);

  const sendCode = useCallback(
    async (isResend) => {
      setBusy(true);
      setError("");
      setNotice("");
      try {
        const res = await api.accessLinkRequestOtp(token);
        setInfo((prev) => ({ ...prev, ...res.data }));
        setResendIn(Math.ceil((res.data.resendAfterMs || 60000) / 1000));
        setStage("code");
        setCode("");
        if (isResend) setNotice("A new code is on its way.");
      } catch (err) {
        const data = err.response?.data;
        if (data?.retryAfterMs) {
          setResendIn(Math.ceil(data.retryAfterMs / 1000));
          // A cooldown means a code is already in their inbox — move them on to
          // the entry step rather than stranding them on a red banner.
          setStage("code");
        }
        setError(data?.error || "Could not send the code. Try again.");
      } finally {
        setBusy(false);
      }
    },
    [token],
  );

  const verify = async (e) => {
    e.preventDefault();
    if (code.length !== 6 || busy) return;
    setBusy(true);
    setError("");
    setNotice("");
    try {
      const res = await api.accessLinkVerify(token, code);
      // Hand the session to useAuth exactly as a password login would, then let
      // the router take them in. No reload: the app re-renders authenticated.
      auth.adoptSession(res.data);
      navigate("/agents", { replace: true });
    } catch (err) {
      setError(err.response?.data?.error || "That code did not work.");
      setCode("");
      codeRef.current?.focus();
      setBusy(false);
    }
  };

  return (
    <div
      className="wrapper__root__page"
      style={{ backgroundImage: `url(/login_bg.svg)` }}
    >
      <div className="login-card wrapper__login__card">
        <div className="login__card__inner d-flex flex-column mb__32 gap__12">
          <div className="login__logo__col pos__rel in-flex align-items-center justify-content-center">
            <img
              src="/saffronos.png"
              alt="SaffronOS Logo"
              style={{
                height: "46px",
                width: "auto",
                filter:
                  "brightness(1.12) drop-shadow(0 0 10px rgba(6,182,212,0.2))",
              }}
            />
            <span className="betaTag">Beta</span>
          </div>

          <div className="login-ai-badge">Organic search with AI, GEO etc.</div>

          {stage !== "loading" && (
            <div className="text__center">
              <p className="fb__14 fc__5F636A">
                {stage === "dead"
                  ? "This link isn't usable"
                  : stage === "code"
                    ? "Enter the code we emailed you"
                    : "Sign in to your guest workspace"}
              </p>
            </div>
          )}
        </div>

        {stage === "loading" && (
          <div className="text__center">
            <span
              className="spinner"
              style={{ width: "22px", height: "22px", borderWidth: "2px" }}
            />
            <p className="fb__13 fc__5F636A" style={{ marginTop: "10px" }}>
              Checking your link…
            </p>
          </div>
        )}

        {stage === "dead" && (
          <>
            <div className="alert alert-error">
              <span>⚠</span> {error}
            </div>
            <p className="fb__13 fc__5F636A text__center">
              It may have been revoked, or replaced by a newer one.
            </p>
          </>
        )}

        {stage === "request" && (
          <div className="login__form">
            <p
              className="fb__14 fc__5F636A text__center"
              style={{ marginBottom: "20px", lineHeight: 1.6 }}
            >
              We'll email a 6-digit code to{" "}
              <strong style={{ color: "var(--text-heading)" }}>
                {info?.emailMasked}
              </strong>
              . No password needed.
            </p>

            {error && (
              <div className="alert alert-error">
                <span>⚠</span> {error}
              </div>
            )}

            <button
              type="button"
              className="common__btn common__org__btn w__100"
              disabled={busy}
              onClick={() => sendCode(false)}
            >
              {busy ? (
                <>
                  <span
                    className="spinner"
                    style={{ width: "16px", height: "16px", borderWidth: "2px" }}
                  />
                  Sending…
                </>
              ) : (
                "Email me a code →"
              )}
            </button>
            <Allowance info={info} />
          </div>
        )}

        {stage === "code" && (
          <form onSubmit={verify} className="login__form">
            <p
              className="fb__14 fc__5F636A text__center"
              style={{ marginBottom: "20px", lineHeight: 1.6 }}
            >
              Sent to{" "}
              <strong style={{ color: "var(--text-heading)" }}>
                {info?.emailMasked}
              </strong>
              . It expires in 10 minutes.
            </p>

            {error && (
              <div className="alert alert-error">
                <span>⚠</span> {error}
              </div>
            )}
            {notice && !error && (
              <p
                className="fb__13 fc__E57600 text__center"
                style={{ marginBottom: "12px" }}
              >
                {notice}
              </p>
            )}

            <div className="form-group">
              <label>Sign-in code</label>
              <input
                ref={codeRef}
                // Numeric keypad on mobile; six digits is a code, not a word.
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                placeholder="000000"
                value={code}
                disabled={busy}
                onChange={(e) => {
                  const next = e.target.value.replace(/\D/g, "").slice(0, 6);
                  setCode(next);
                  if (error) setError("");
                }}
                style={{
                  textAlign: "center",
                  fontSize: "26px",
                  fontWeight: 700,
                  letterSpacing: "0.34em",
                  // Trailing letter-spacing shifts the text off-centre; pull it
                  // back so the digits sit in the middle of the field.
                  textIndent: "0.34em",
                  fontFamily: "'JetBrains Mono', Menlo, monospace",
                }}
              />
            </div>

            <button
              type="submit"
              className="common__btn common__org__btn w__100"
              disabled={busy || code.length !== 6}
            >
              {busy ? (
                <>
                  <span
                    className="spinner"
                    style={{ width: "16px", height: "16px", borderWidth: "2px" }}
                  />
                  Verifying…
                </>
              ) : (
                "Sign In →"
              )}
            </button>

            <div className="text__center" style={{ marginTop: "16px" }}>
              <button
                type="button"
                className="link-button"
                onClick={() => sendCode(true)}
                disabled={busy || resendIn > 0}
                style={resendIn > 0 ? { color: "var(--text-muted)" } : undefined}
              >
                {resendIn > 0
                  ? `Resend code in ${resendIn}s`
                  : "Didn't get it? Resend"}
              </button>
            </div>
            <Allowance info={info} />
          </form>
        )}
      </div>
    </div>
  );
}

/** Sets expectations before they sign in, so the limits are never a surprise. */
function Allowance({ info }) {
  if (!info?.ttlDays && !info?.expiresAt) return null;
  // The window starts at creation, so by the time anyone reads this the clock
  // has been running — state the date rather than a duration.
  const until = info?.expiresAt
    ? new Date(info.expiresAt).toLocaleDateString(undefined, {
        day: "numeric",
        month: "long",
        year: "numeric",
      })
    : null;

  return (
    <p
      className="fb__12 fc__5F636A text__center"
      style={{
        marginTop: "22px",
        paddingTop: "16px",
        borderTop: "1px solid var(--border)",
        lineHeight: 1.6,
      }}
    >
      {until ? <>Your access is open until {until}.</> : <>Your access is open for {info.ttlDays} days.</>}
    </p>
  );
}
